// Library
import { useMemo, useState } from 'react';
import { StylesConfig } from 'react-select';
import AsyncSelect from 'react-select/async';

// CSS Style for this module
import styles from './searchField.module.scss';

type Option = { value: string; label: string };

export interface SearchFieldProps {
	/** Custom styling for the container */
	customStyles?: string;
	/** Defaul option of the dropdown list  */
	defaultValue?: string;
	/** Default text that is displayed within the field */
	placeholder?: string;
	/**
	 * Helper text for additional guidance about how the input field will be used.
	 * It should only take up a single line
	 */
	hintText?: string;
	/** Msg to be displayed when the status is error */
	errorMsg?: string;
	/**
	 * Function to invoke on typing
	 */
	searchFunction: (searchTerm: string) => Promise<string[]>;
	/**
	 * onChange function for keeping track field value
	 * debounce is built in
	 */
	handleChange?: (value: string) => void;
	/**
	 * Boolean for determining whether this is a disabled field
	 */
	disabled?: boolean;
	/**
	 * Boolean for determining whether this field has to be autofocused
	 */
	autoFocus?: boolean;
	/** Element identifier selector for test */
	testId?: string;
}

/**
 * SearchField component represents a searchable dropdown list.
 * @param {SearchFieldProps} props - The properties for the SearchField component.
 * @returns {JSX.Element} A searchable dropdown list component.
 */
export const SearchField = ({
	customStyles,
	defaultValue,
	placeholder = 'Search',
	hintText,
	errorMsg,
	disabled,
	autoFocus,
	searchFunction,
	handleChange = () => {
		return;
	},
	testId,
}: SearchFieldProps) => {
	const [defaultShownValue] = useState(defaultValue && capitalize(defaultValue));
	const hasError = useMemo(() => Boolean(errorMsg), [errorMsg]);

	function capitalize(str: string): string {
		return str.charAt(0).toUpperCase() + str.slice(1);
	}

	const fetchOptions = async (searchTerm: string | undefined) =>
		searchTerm
			? await searchFunction(searchTerm)
					.then(options =>
						options.map(option => ({
							value: option,
							label: capitalize(option),
						})),
					)
					.catch(() => [])
			: [];

	const handleSelect = (option: Option) => {
		handleChange(option.value);
	};

	const searchStyles: StylesConfig = {
		control: (styles, { isDisabled, isFocused }) => ({
			...styles,
			boxShadow: 'none', // This line disable the default blue border
			backgroundColor: hasError ? '#FFF6F6' : isDisabled ? ' #e6e6e6;' : '#fff', // $cr-secondary-background-dark
			borderColor: hasError ? '#8b4c36' : isFocused ? '#023956' : 'rgba(88, 88, 90, 0.45)', // $cr-error, $cr-dark-teal, $cr-primary-background-dark
			borderWidth: '2px',
			borderRadius: '4px',
			minHeight: '40px',
			boxSizing: 'content-box',
			pointerEvents: 'auto',
			cursor: isDisabled ? 'not-allowed' : 'pointer',

			outlineColor: '#067296', // $cr-background-cta
			outlineWidth: '3px',
			outlineOffset: '2px',
			outline: isFocused ? 'solid' : 'none',

			':hover': {
				borderColor: isDisabled ? 'auto' : '#023956', // $cr-dark-teal
				backgroundColor: isDisabled ? 'auto' : '#f2f2f2', // darken(white, 5%)
			},
		}),
		singleValue: styles => ({
			...styles,
			color: '#333333', // $cr-subtext
		}),
		option: (styles, { isFocused, isSelected }) => ({
			...styles,
			backgroundColor: isSelected ? '#dcf6ff' : isFocused ? '#f2f2f2' : '#fff', // $cr-selected-option, darken(white, 5%)
			color: '#333333', // $cr-subtext
			fontWeight: isSelected ? 'bold' : 'normal',
			cursor: 'pointer',
		}),
		indicatorSeparator: styles => ({
			...styles,
			display: 'none',
		}),
		dropdownIndicator: styles => ({
			...styles,
			display: 'none',
			// color: 'rgba(88, 88, 90, 0.45)', // $cr-primary-background-dark
		}),
		menu: (styles, { isLoading, options }) => ({
			...styles,
			display: isLoading || options?.length === 0 ? 'none' : 'block',
		}),
		loadingIndicator: styles => ({
			...styles,
			color: '#067296', // $cr-background-cta
			fontSize: '5px',
		}),
	};

	return (
		<div className={[styles.container, customStyles].join(' ')}>
			{hintText && <span className={styles.hint}>{hintText}</span>}
			{hasError && <p className={styles['error-msg']}>{errorMsg}</p>}
			<AsyncSelect
				name="searchField"
				styles={searchStyles}
				defaultInputValue={defaultShownValue}
				onChange={(newValue: unknown) => newValue && handleSelect(newValue as Option)}
				placeholder={!disabled && placeholder}
				isDisabled={disabled}
				autoFocus={autoFocus}
				theme={theme => ({
					...theme,
					colors: {
						...theme.colors,
						primary: '#dcf6ff', // $cr-selected-option | Background dropdown color on click
						primary50: '#dcf6ff', // $cr-selected-option | Background dropdown color on click
					},
				})}
				isSearchable={true}
				cacheOptions
				loadOptions={fetchOptions}
				data-testid={testId}
			/>
		</div>
	);
};
